'use client';

/**
 * GlobalLeadsAlertBar
 *
 * A mind-blowing, attention-grabbing animated notification bar that lives at
 * the top of every page (mounted by the root layout). It promotes
 * gptleadshunter.com as the dedicated lead-discovery engine with a
 * prominent, animated CTA button.
 *
 * It also installs a global window.fetch wrapper that gracefully converts
 * non-JSON server error responses (e.g. raw "Internal Server Error" HTML pages)
 * into clean JSON envelopes — so the rest of the app never again surfaces
 * the cryptic "Unexpected token 'I', \"Internal S\"... is not valid JSON" error.
 * Existing callers that do `(await fetch(...).json())` keep working unchanged,
 * but now receive { success: false, error: '<readable server message>' }.
 *
 * The bar is dismissable. The dismissal is remembered in localStorage so it
 * does not nag the same user every reload.
 */

import { useEffect, useState } from 'react';
import FindLeadsButton from '@/components/FindLeadsButton';

const DISMISS_KEY = 'gpt-leads-alert-bar-dismissed-at';
// Show again after 24 hours, so users who dismissed it still get a periodic reminder.
const REMIND_AFTER_MS = 24 * 60 * 60 * 1000;

export default function GlobalLeadsAlertBar() {
    const [visible, setVisible] = useState(false);

    // === Visibility (respect dismissal) ===
    useEffect(() => {
        let alive = true;
        // Owner-only: this bar promotes the owner's OWN lead product, so show it ONLY on
        // the product owner's install. A rebranded client build (tier !== 'owner') never
        // sees it. (The global fetch-resilience patch in the next effect runs regardless.)
        fetch('/api/license/status').then(r => r.json()).then(d => {
            if (!alive) return;
            if (!(d && d.success && d.tier === 'owner')) return;   // client build -> stay hidden
            try {
                const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
                if (!dismissedAt || Date.now() - dismissedAt > REMIND_AFTER_MS) setVisible(true);
            } catch { setVisible(true); }
        }).catch(() => {});
        return () => { alive = false; };
    }, []);

    // === Global fetch resilience patch ===
    // Wrap window.fetch so that response.json() never throws on HTML error pages.
    // We patch json() on the returned Response so all existing .json() callers
    // (~68 sites in the app) get a clean JSON error envelope instead.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.__leadsHunterFetchPatched) return;
        window.__leadsHunterFetchPatched = true;

        const originalFetch = window.fetch.bind(window);
        window.fetch = async (...args) => {
            const res = await originalFetch(...args);
            const originalJson = res.json.bind(res);
            res.json = async () => {
                // Read once, then decide how to parse.
                const text = await res.clone().text();
                if (!text) {
                    return { success: res.ok, error: res.ok ? undefined : `Server returned an empty ${res.status} response.` };
                }
                try {
                    return JSON.parse(text);
                } catch {
                    // The server returned non-JSON (almost always an HTML error page like
                    // "Internal Server Error" from Next.js when a route fails to load).
                    // Extract a short human-friendly message instead of crashing the caller.
                    let msg = text.trim();
                    // Pull a <title> or first <h1> if it looks like HTML.
                    const titleMatch = msg.match(/<title[^>]*>([^<]+)<\/title>/i);
                    const h1Match = msg.match(/<h1[^>]*>([^<]+)<\/h1>/i);
                    if (titleMatch) msg = titleMatch[1].trim();
                    else if (h1Match) msg = h1Match[1].trim();
                    else if (msg.length > 240) msg = msg.slice(0, 240) + '…';
                    return {
                        success: false,
                        error: `Server error (${res.status}): ${msg}`,
                        _nonJson: true,
                        _httpStatus: res.status
                    };
                }
            };
            return res;
        };
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch {}
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes ghBarSlideDown {
                    from { opacity: 0; transform: translateY(-100%); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes ghBarShimmer {
                    0%   { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                @keyframes ghBarBgPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(251,146,60,0); inset 0 -1px 0 rgba(251,146,60,0.35); }
                    50%      { box-shadow: 0 4px 30px -10px rgba(251,146,60,0.45); }
                }
                @keyframes ghBarIconBounce {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    25%      { transform: translateY(-3px) rotate(-6deg); }
                    50%      { transform: translateY(0) rotate(0deg); }
                    75%      { transform: translateY(-3px) rotate(6deg); }
                }
                @keyframes ghBarBtnGlow {
                    0%, 100% { box-shadow: 0 4px 20px rgba(251,146,60,0.4), 0 0 0 0 rgba(251,146,60,0.6); }
                    50%      { box-shadow: 0 4px 24px rgba(251,146,60,0.6), 0 0 0 8px rgba(251,146,60,0); }
                }
                @keyframes ghBarMarquee {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                @keyframes ghBarPingDot {
                    0%   { transform: scale(1);   opacity: 1; }
                    100% { transform: scale(2.6); opacity: 0; }
                }
                .gh-alert-bar {
                    animation: ghBarSlideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .gh-alert-bar .gh-cta:hover {
                    transform: translateY(-1px) scale(1.04) !important;
                    filter: brightness(1.08);
                }
                @media (max-width: 760px) {
                    .gh-alert-bar .gh-marquee-wrap { display: none !important; }
                    .gh-alert-bar .gh-static-msg   { display: inline !important; }
                }
            `}} />

            <div
                className="gh-alert-bar"
                role="region"
                aria-label="Promotional notification"
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 200,
                    width: '100%',
                    padding: '10px 18px 10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    background:
                        'linear-gradient(100deg, #1d4ed8 0%, #4f46e5 48%, #7c3aed 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'ghBarShimmer 10s linear infinite',
                    borderBottom: '1px solid rgba(124,58,237,0.55)',
                    boxShadow: '0 6px 20px -8px rgba(79,70,229,0.5)',
                    color: '#ffffff',
                    overflow: 'hidden'
                }}
            >
                {/* Animated icon + live ping */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                        width: '34px', height: '34px', borderRadius: '10px',
                        background: 'linear-gradient(135deg, #fb923c 0%, #eab308 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '18px',
                        boxShadow: '0 4px 14px rgba(251,146,60,0.45)',
                        animation: 'ghBarIconBounce 2.2s ease-in-out infinite'
                    }}>🎯</div>
                    <span aria-hidden="true" style={{
                        position: 'absolute', top: '-3px', right: '-3px', width: '10px', height: '10px',
                        borderRadius: '50%', background: '#10b981',
                        boxShadow: '0 0 0 2px rgba(8,7,17,0.9)'
                    }}>
                        <span style={{
                            position: 'absolute', inset: 0, borderRadius: '50%',
                            background: '#10b981',
                            animation: 'ghBarPingDot 1.4s ease-out infinite'
                        }} />
                    </span>
                </div>

                {/* Message — scrolling marquee on wide screens, static on small */}
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div className="gh-marquee-wrap" style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                        <div style={{
                            display: 'flex',
                            gap: '48px',
                            animation: 'ghBarMarquee 28s linear infinite',
                            paddingRight: '48px',
                            // Size to CONTENT and never let the flex parent shrink the
                            // track — otherwise translateX(-50%) is relative to a clamped
                            // width and the two copies overlap (worse in longer languages).
                            width: 'max-content',
                            flexShrink: 0,
                        }}>
                            {/* Two copies so the marquee can loop seamlessly */}
                            {[0, 1].map((k) => (
                                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '14px', fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.01em', flexShrink: 0 }}>
                                    <span style={{
                                        fontSize: '10px', fontWeight: 900, letterSpacing: '0.1em',
                                        padding: '2px 8px', borderRadius: '100px',
                                        background: 'rgba(251,146,60,0.25)',
                                        border: '1px solid rgba(251,146,60,0.5)',
                                        color: '#c2410c', textTransform: 'uppercase', flexShrink: 0
                                    }}>
                                        🔥 Limited Boost
                                    </span>
                                    <span>
                                        Get <span style={{
                                            background: 'linear-gradient(90deg, #fbbf24, #f97316, #fbbf24)',
                                            backgroundSize: '200% auto',
                                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                            animation: 'ghBarShimmer 3s linear infinite',
                                            fontWeight: 900
                                        }}>thousands of leads</span> — search &amp; download verified leads with the <span style={{ color: '#ea580c', fontWeight: 800 }}>Optilink AI Lead Scraper</span>
                                    </span>
                                    <span style={{ color: '#d97706' }}>✦</span>
                                    <span style={{ color: '#fde68a', fontWeight: 700 }}>
                                        Verified emails, phones &amp; socials for any niche
                                    </span>
                                    <span style={{ color: '#d97706' }}>✦</span>
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* Mobile-only static version */}
                    <span className="gh-static-msg" style={{
                        display: 'none', fontSize: '13px', fontWeight: 700, color: '#ffffff'
                    }}>
                        Get <strong style={{ color: '#fde68a' }}>thousands of leads</strong> — with the Optilink AI Lead Scraper
                    </span>
                </div>

                {/* CTA — launches the local Lead Scraper (was a link to the website) */}
                <FindLeadsButton
                    label="🚀 Get Leads Now"
                    style={{
                        flexShrink: 0,
                        padding: '9px 18px',
                        background: 'linear-gradient(135deg, #fb923c 0%, #eab308 100%)',
                        color: '#111',
                        fontSize: '13px', fontWeight: 900,
                        whiteSpace: 'nowrap',
                        animation: 'ghBarBtnGlow 2s ease-in-out infinite',
                        letterSpacing: '0.02em',
                        textShadow: '0 1px 0 rgba(15,23,42,0.25)',
                    }}
                />

                {/* Dismiss */}
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss notification"
                    title="Dismiss for 24h"
                    style={{
                        flexShrink: 0,
                        width: '28px', height: '28px', borderRadius: '8px',
                        background: 'rgba(255,255,255,0.16)',
                        border: '1px solid rgba(15, 23, 42, 0.35)',
                        color: '#ffffff',
                        cursor: 'pointer',
                        fontSize: '14px', lineHeight: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.16)'; }}
                >
                    ✕
                </button>
            </div>
        </>
    );
}
